import {Node} from 'effector'
import {GroupRecord} from '../group'
import {Border, UnitCell, AnyRuntimeUnit} from './index.h'

type FileBlock = {
  file: string
  cells: UnitCell[]
  borderSize: Border
}

type DirBlock = {
  dir: string
  fileGroups: FileBlock[]
  borderSize: Border
  inputs: UnitCell[]
  outputs: UnitCell[]
  lines: [UnitCell, UnitCell][]
}

type Dataflow = {
  id: number
  nodes: string[]
}

const maxDirWidth = 8
const maxDirHeight = 10
const maxFieldWidth = 66
const GAP = 1

export function computeFullData(
  map: Record<string, AnyRuntimeUnit>,
  dirList: GroupRecord<GroupRecord<AnyRuntimeUnit>>[]
) {
  const namedGraph = {} as Record<string, string>
  for (const unitId in map) {
    const e = map[unitId]
    const {meta} = e.node
    if (meta.namedChilds) {
      for (const child of meta.namedChilds) {
        namedGraph[child] = unitId
      }
    }
  }
  for (const unitId in namedGraph) {
    let currentId = namedGraph[unitId]
    while (namedGraph[currentId]) {
      currentId = namedGraph[currentId]
    }
    if (unitId !== currentId) {
      namedGraph[unitId] = currentId
    }
  }
  const fileGraph = {} as Record<string, string[]>
  for (const unitId in map) {
    const e = map[unitId]
    const {meta} = e.node
    if (meta.file) {
      let file = meta.file as string
      if (file.startsWith('/')) file = file.slice(1)
      fileGraph[unitId] = file.split(/\//g)
    }
  }
  const dirFiles = {} as Record<string, string[]>
  const fileDir = {} as Record<string, string>
  for (const key in fileGraph) {
    const fileSeg = fileGraph[key]
    const file = fileSeg.join('/')
    if (file in fileDir) continue
    const dir = fileSeg.slice(0, -1).join('/')
    fileDir[file] = dir
    addToMapset(dirFiles, dir, file)
  }
  function isSimpleComputed(node: Node) {
    switch (node.meta.createdBy) {
      case 'store.map':
      case 'event.map':
      case 'event.prepend':
      case 'event.filter':
      case 'event.filterMap':
      case 'fx.map':
      case 'fx.prepend':
      case 'fx.filter':
      case 'fx.filterMap':
      case 'restore':
        return true
      default:
        return false
    }
  }
  const createdFromGraph = {} as Record<string, string>
  function resolveCreatorId(unitId: string) {
    return createdFromGraph[unitId] || unitId
  }
  for (let unitId in map) {
    const sourceId = unitId
    let node = map[unitId].node
    while (unitId in namedGraph || isSimpleComputed(node)) {
      while (unitId in namedGraph) {
        unitId = namedGraph[unitId]
      }
      node = map[unitId].node
      while (isSimpleComputed(node)) {
        unitId = node.meta.depsMap.source
        node = map[unitId].node
      }
    }
    if (unitId !== sourceId) {
      createdFromGraph[sourceId] = unitId
    }
  }
  const dataflows = [] as Dataflow[]
  const unitLinks = [] as Array<{from: string; to: string}>
  for (const unitId in map) {
    const e = map[unitId]
    const {meta} = e.node
    const dataflow: Dataflow = {
      id: dataflows.length,
      nodes: []
    }
    if (e.unit === 'sample') {
      const {snap} = e.node.meta
      if (snap.targeting === 'new unit') continue
      if (snap.sourceType.type !== 'single') continue
      if (
        snap.targeting === 'existed unit' &&
        snap.sourceType.type === 'single'
      ) {
        unitLinks.push({
          from: resolveCreatorId(snap.sourceType.id),
          to: resolveCreatorId(snap.targetId)
        })
      }
      for (const id of snap.sourceIds.map(resolveCreatorId)) {
        dataflow.nodes.push(id)
      }
      if (snap.clockType.type === 'explicit') {
        const clock = resolveCreatorId(snap.clockType.id)
        dataflow.nodes.push(clock)
      }
      const target = resolveCreatorId(snap.targetId)
      dataflow.nodes.push(target)
    }
    switch (meta.createdBy) {
      case 'forward': {
        let from = meta.depsMap.from
        let to = meta.depsMap.to
        if (from && to && !Array.isArray(from) && !Array.isArray(to)) {
          unitLinks.push({
            from: resolveCreatorId(from),
            to: resolveCreatorId(to)
          })
        }
        if (from) {
          if (Array.isArray(from)) {
            from = from.map(resolveCreatorId)
            dataflow.nodes.push(...from)
          } else {
            from = resolveCreatorId(from)
            dataflow.nodes.push(from)
          }
        }
        // dataflow.nodes.push(meta.unitId)
        if (to) {
          if (Array.isArray(to)) {
            to = to.map(resolveCreatorId)
            dataflow.nodes.push(...to)
          } else {
            to = resolveCreatorId(to)
            dataflow.nodes.push(to)
          }
        }
        break
      }
      case 'guard': {
        break
        if (!meta.internalNode) break
        let {source, filter, target, filterIsUnit} = meta.depsMap
        source = resolveCreatorId(source)
        dataflow.nodes.push(source)
        if (filterIsUnit) {
          filter = resolveCreatorId(filter)
          dataflow.nodes.push(filter)
        }
        target = resolveCreatorId(target)
        dataflow.nodes.push(target)
        break
      }
      case 'on': {
        if (e.unit === 'on') {
          const source = resolveCreatorId(e.source)
          const clock = resolveCreatorId(e.clock)
          unitLinks.push({
            from: source,
            to: clock
          })
          break
          dataflow.nodes.push(source)
          dataflow.nodes.push(clock)
        }
        break
      }
      case 'combine': {
        for (const id of Object.values(meta.depsMap.source).map(
          resolveCreatorId
        )) {
          unitLinks.push({
            from: id,
            to: resolveCreatorId(meta.unitId)
          })
          dataflow.nodes.push(id)
        }
        break
        dataflow.nodes.push(meta.unitId)
        dataflow.nodes = [...new Set(dataflow.nodes)]
        break
      }
      case 'attach': {
        break
        if (meta.depsMap.hasSource) {
          for (const id of meta.depsMap.store.ids.map(resolveCreatorId)) {
            dataflow.nodes.push(id)
          }
        }
        const effect = resolveCreatorId(meta.depsMap.effect)
        const target = resolveCreatorId(meta.unitId)
        dataflow.nodes.push(effect, target)
        break
      }
      case 'createApi':
      case 'split': {
        break
        if (meta.createdBy === 'split') console.count('split')
        if (!meta.depsMap.source) break
        const source = resolveCreatorId(meta.depsMap.source)
        const unitId = resolveCreatorId(meta.unitId)
        dataflow.nodes.push(source, unitId)
        break
      }
    }
    if (dataflow.nodes.length > 0) dataflows.push(dataflow)
  }

  const unitDepsIn = {} as Record<string, string[]>
  const unitDepsOut = {} as Record<string, string[]>
  const fileDepsIn = {} as Record<string, Record<string, string[]>>
  const fileDepsOut = {} as Record<string, Record<string, string[]>>
  const dirDepsIn = {} as Record<string, Record<string, string[]>>
  const dirDepsOut = {} as Record<string, Record<string, string[]>>
  for (const {from, to} of unitLinks) {
    if (!fileGraph[from] || !fileGraph[to]) continue
    const fromFile = fileGraph[from].join('/')
    const toFile = fileGraph[to].join('/')
    if (fromFile === toFile) continue
    addToMapset(unitDepsOut, from, to)
    addToMapset(unitDepsIn, to, from)
    addToNestedMapset(fileDepsOut, [fromFile, toFile], from)
    addToNestedMapset(fileDepsIn, [toFile, fromFile], to)
  }
  for (const fromFile in fileDepsOut) {
    const fromDir = fileDir[fromFile]
    for (const toFile in fileDepsOut[fromFile]) {
      const toDir = fileDir[toFile]
      if (fromDir === toDir) continue
      for (const from of fileDepsOut[fromFile][toFile]) {
        addToNestedMapset(dirDepsOut, [fromDir, toDir], from)
      }
    }
  }
  for (const toFile in fileDepsIn) {
    const toDir = fileDir[toFile]
    for (const fromFile in fileDepsIn[toFile]) {
      const fromDir = fileDir[fromFile]
      if (fromDir === toDir) continue
      for (const to of fileDepsIn[toFile][fromFile]) {
        addToNestedMapset(dirDepsIn, [toDir, fromDir], to)
      }
    }
  }
  function createDirGroup({
    groupKey: dir,
    values: files
  }: GroupRecord<GroupRecord<AnyRuntimeUnit>>): DirBlock | null {
    let startRow = 0
    let freeSpace = maxDirWidth
    const fileGroups = [] as FileBlock[]
    files.forEach(({groupKey: file, values}, i) => {
      const isLastFile = i === files.length - 1
      values = values.filter(e => {
        switch (e.node.meta.createdBy) {
          case 'split':
          case 'createApi':
          case 'event.map':
          case 'event.prepend':
          case 'event.filter':
          case 'event.filterMap':
          case 'fx.prepend':
          case 'fx.map':
          case 'store.map':
            return false
          default:
            return true
        }
      })
      if (values.length === 0) return
      const isLargeFile = values.length >= maxDirWidth
      const isFitFreeSpace = values.length <= freeSpace
      if (!isFitFreeSpace) {
        if (i !== 0) {
          startRow += 2
        }
        freeSpace = maxDirWidth
      }
      let cell = maxDirWidth - freeSpace
      let row = startRow
      const cells = values.map(
        (e, i): UnitCell => {
          const color = (type => {
            switch (type) {
              case 'forward':
                return style.color.red
              case 'effect':
                return 'darkorange'
              case 'event':
                return style.color.gray
              default:
                return style.color.black
            }
          })(e.unit)
          const x = cell
          const y = row
          cell += 1
          if (cell >= maxDirWidth) {
            cell = 0
            if (i !== values.length - 1) {
              row += 1
              startRow += 1
            }
          }
          return {
            id: i + idPrefix,
            x,
            y,
            color,
            tag: resolveCreatorId(e.node.meta.unitId)
          }
        }
      )

      const xs = cells.map(({x}) => x)
      const ys = cells.map(({y}) => y)
      const x = Math.min(...xs)
      const y = Math.min(...ys)
      const width = Math.max(...xs) - x + 1
      const height = Math.max(...ys) - y + 1

      if (freeSpace - width > 1) {
        freeSpace -= width + 1
      } else {
        freeSpace = maxDirWidth
        if (!isLargeFile && !isLastFile) {
          startRow += 2
        }
      }
      if (isLargeFile && !isLastFile) {
        startRow += 2
      }
      idPrefix += values.length
      fileGroups.push({
        file,
        cells,
        borderSize: {x, y, width, height}
      })
    })
    if (fileGroups.length === 0) return null
    const xs = fileGroups.map(({borderSize}) => borderSize.x)
    const ys = fileGroups.map(({borderSize}) => borderSize.y)
    const lastXs = fileGroups.map(({borderSize: {x, width}}) => x + width)
    const lastYs = fileGroups.map(({borderSize: {y, height}}) => y + height)
    const x = Math.min(...xs)
    const y = Math.min(...ys)
    const width = Math.max(...lastXs) - x
    const height = Math.max(...lastYs) - y
    return {
      dir,
      fileGroups,
      borderSize: {x, y, width, height},
      inputs: [],
      outputs: [],
      lines: []
    }
  }
  let idPrefix = 0
  let nextRow = 0
  let nextColumn = 0
  let tallestRectInRow = 0
  const blocks = [] as DirBlock[]
  dirList.forEach(dirGroup => {
    let dirname = dirGroup.groupKey
    if (dirname.startsWith('/')) dirname = dirname.slice(1)
    const isolatedDir = createDirGroup(dirGroup)
    if (!isolatedDir) return
    let paddingLeft = 0
    let paddingRight = 0
    const depsIn = dirDepsIn[dirname]
    const depsOut = dirDepsOut[dirname]
    if (depsIn) {
      const depsFiles = Object.keys(depsIn)
      const blockHeight =
        depsFiles.length >= maxDirHeight ? maxDirHeight : depsFiles.length

      const workingHeight = Math.max(blockHeight, isolatedDir.borderSize.height)
      isolatedDir.borderSize.height = workingHeight
      paddingLeft = Math.ceil(depsFiles.length / workingHeight) + 1
      for (const depsFile in depsIn) {
        const index = isolatedDir.inputs.length
        const row = index % workingHeight
        const col = (index - row) / workingHeight
        const cell: UnitCell = {
          id: ++idPrefix,
          x: col,
          y: row,
          color: style.color.black,
          tag: `in_${depsFile}`
        }
        isolatedDir.inputs.push(cell)
      }
    }
    if (depsOut) {
      const depsFiles = Object.keys(depsOut)
      const blockHeight =
        depsFiles.length >= maxDirHeight ? maxDirHeight : depsFiles.length
      const workingHeight = Math.max(blockHeight, isolatedDir.borderSize.height)
      isolatedDir.borderSize.height = workingHeight
      paddingRight = Math.ceil(depsFiles.length / workingHeight) + 1
      const firstCol = isolatedDir.borderSize.width + paddingLeft + 1
      for (const depsFile in depsOut) {
        const index = isolatedDir.outputs.length
        const row = index % workingHeight
        const col = firstCol + (index - row) / workingHeight
        const cell: UnitCell = {
          id: ++idPrefix,
          x: col,
          y: row,
          color: style.color.black,
          tag: `out_${depsFile}`
        }
        isolatedDir.outputs.push(cell)
      }
    }
    if (depsIn && depsOut) {
      console.log({depsIn, depsOut, dir: isolatedDir})
    }
    const freeSpace = maxFieldWidth - nextColumn
    const isFitFreeSpace = isolatedDir.borderSize.width <= freeSpace
    if (!isFitFreeSpace) {
      nextRow += tallestRectInRow
      nextColumn = 0
      tallestRectInRow = 0
    }
    const dir = moveDirBlock({
      dir: isolatedDir,
      x: nextColumn,
      y: nextRow,
      padding: {
        top: 0,
        bottom: 0,
        left: 0,
        right: 0
      },
      paddingFiles: {
        left: paddingLeft,
        right: paddingRight
      }
    })
    const cellMap = {} as Record<string, UnitCell>
    dir.fileGroups.forEach(group => {
      group.cells.forEach(cell => {
        cellMap[cell.tag] = cell
      })
    })
    if (depsIn) {
      for (const depsFile in depsIn) {
        const fromCell = dir.inputs.find(e => e.tag === `in_${depsFile}`)
        if (!fromCell) {
          // console.warn({depsFile, dir, depsIn})
          continue
        }
        for (const unitId of depsIn[depsFile]) {
          dir.lines.push([fromCell, cellMap[unitId]])
        }
      }
    }
    if (depsOut) {
      for (const depsFile in depsOut) {
        const toCell = dir.outputs.find(e => e.tag === `out_${depsFile}`)!
        for (const unitId of depsOut[depsFile]) {
          if (!cellMap[unitId]) {
            // console.warn({cellMap, unitId, toCell, depsFile, depsOut, dir})
            continue
          }
          dir.lines.push([cellMap[unitId], toCell])
        }
      }
    }
    const height = dir.borderSize.height + GAP
    const width = dir.borderSize.width + GAP
    if (isFitFreeSpace) {
      tallestRectInRow = Math.max(tallestRectInRow, height)
      if (nextColumn + width < freeSpace) {
        nextColumn += width
      } else {
        nextRow += tallestRectInRow
        nextColumn = 0
        tallestRectInRow = 0
      }
    } else {
      nextColumn += width
    }
    blocks.push(dir)
  })

  return {
    // namedUnitMap: namedGraph,
    // unitFileMap: fileGraph,
    // createdFromMap: createdFromGraph,
    // dataflows,
    dirGroups: blocks
  }
}

function addToNestedMapset<T>(
  mapset: Record<string, Record<string, T[]>>,
  [a, b]: [string, string],
  value: T
) {
  let nested = mapset[a]
  if (!nested) mapset[a] = nested = {}
  let set = nested[b]
  if (!set) nested[b] = set = []
  if (!set.includes(value)) set.push(value)
}
function addToMapset<T>(mapset: Record<string, T[]>, key: string, value: T) {
  let set: T[] = mapset[key]
  if (!set) mapset[key] = set = []
  if (!set.includes(value)) set.push(value)
}

function moveDirBlock({
  x,
  y,
  dir: {dir, fileGroups, borderSize, inputs, outputs, lines},
  padding = {},
  paddingFiles = {}
}: {
  x: number
  y: number
  dir: DirBlock
  padding?: {
    top?: number
    bottom?: number
    left?: number
    right?: number
  }
  paddingFiles?: {
    left?: number
    right?: number
  }
}): DirBlock {
  const {
    top: paddingTop = 0,
    bottom: paddingBottom = 0,
    left: paddingLeft = 0,
    right: paddingRight = 0
  } = padding
  const {left: padFilesL = 0, right: padFilesR = 0} = paddingFiles
  const dxNoPad = x + paddingLeft - borderSize.x
  const dyNoPad = y - borderSize.y
  const dx = dxNoPad + padFilesL
  const dy = y + paddingTop - borderSize.y
  const moveCell = (
    {id, x, y, color, tag}: UnitCell,
    needPadding: boolean
  ): UnitCell => ({
    id,
    x: x + (needPadding ? dx : dxNoPad),
    y: y + (needPadding ? dy : dy),
    color,
    tag
  })
  return {
    dir,
    fileGroups: fileGroups.map(
      ({file, cells, borderSize}): FileBlock => ({
        file,
        cells: cells.map(cell => moveCell(cell, true)),
        borderSize: {
          x: borderSize.x + dx,
          y: borderSize.y + dy,
          width: borderSize.width,
          height: borderSize.height
        }
      })
    ),
    borderSize: {
      x,
      y,
      width:
        borderSize.width + paddingLeft + paddingRight + padFilesL + padFilesR,
      height: borderSize.height + paddingTop + paddingBottom
    },
    inputs: inputs.map(cell => moveCell(cell, false)),
    outputs: outputs.map(cell => moveCell(cell, false)),
    lines: lines.map(([from, to]) => [
      moveCell(from, false),
      moveCell(to, true)
    ])
  }
}

const style = {
  color: {
    red: 'firebrick',
    black: '#343434',
    gray: '#747474',
    white: 'oldlace'
  }
}
